import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  LueftungNetzbefundmodell, LueftungNetzbefundzeile, LueftungPruefantwort, LueftungRegelbezug,
  LueftungSeitenzeile,
} from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, FilterBar, KpiCard, KpiReihe, LoadingState,
  StatusBadge, Vorbehalte, bestaetigung, formatiereZahl, useFilterInUrl, type FilterDefinition, type Spalte,
} from '../../muster';
import {
  AnnahmenAbschnitt, Elementverweis, Rechenstand, befundSeiten, optionenAus, schwereStatus, seitenVorbehalte,
  zahlNachEinheit,
} from './pruefung';

type Antwort = LueftungPruefantwort<LueftungNetzbefundmodell, LueftungNetzbefundzeile>;
/** Die Seitenzeile mit ihrer Stelle im Lauf - der stabile Schlüssel der Tabelle. */
type Zeile = LueftungSeitenzeile<LueftungNetzbefundzeile> & { nr: number };
type Regelzeile = LueftungRegelbezug & { fehler: number; warnungen: number; hinweise: number };

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BEFUNDE DRUCKVERLUST, SCHALL UND HYGIENE (TV5, Lauf druckverlust-lueftung)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eine reine BEFUNDLISTE (Weisung der Hauptsitzung 18.10.2026): alle Befunde des
 * Druckverlustlaufs ohne Kappung - Geschwindigkeiten, Kennfelder, Regler,
 * Raumpegel, Hygiene -, je mit Normbezug, Verweis auf das Bauteil und auf die
 * Fachseite (Druckverlust, Schall, Raumschall, Anlagenliste). Netz- und
 * Wegtabellen stehen NICHT hier; sie gehören zu den Seiten Druckverlust und
 * Schall (Paket TV6).
 *
 * Die Raumpegelbefunde tragen den Vorbehalt der offenen Eigentümerfrage LUE-39
 * (Regler-Drosseldruck). Sie wird hier ausgewiesen, nicht entschieden.
 */
export function LueftungPruefbefunde() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(() => {
    setLaedt(true);
    setFehler(null);
    api.lueftungPruefbefunde(projekt.id)
      .then(setDaten)
      .catch(setFehler)
      .finally(() => setLaedt(false));
  }, [api, projekt.id]);

  useEffect(() => { laden(); }, [laden]);

  const zeilen = useMemo<Zeile[]>(() => (daten?.zeilen ?? []).map((z, nr) => ({ ...z, nr })), [daten]);
  const m = daten?.modell ?? null;
  const norm = useMemo(
    () => new Map((m?.regeln ?? []).map((r) => [r.regel, r] as const)),
    [m],
  );

  const filter = useMemo<FilterDefinition[]>(() => [
    {
      schluessel: 'schwere', beschriftung: 'Schwere', art: 'mehrfachauswahl',
      optionen: [
        { wert: 'fehler', beschriftung: 'Fehler' }, { wert: 'warnung', beschriftung: 'Warnung' },
        { wert: 'hinweis', beschriftung: 'Hinweis' },
      ],
    },
    {
      schluessel: 'regel', beschriftung: 'Regel', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(zeilen.map((z) => z.zeile.regel)),
    },
    {
      schluessel: 'anlage', beschriftung: 'Anlage', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(zeilen.map((z) => z.zeile.anlage)),
    },
    { schluessel: 'raum', beschriftung: 'Raum', art: 'text', platzhalter: 'z. B. E1-207' },
  ], [zeilen]);
  const [werte, setWerte] = useFilterInUrl(filter);

  const gefiltert = useMemo(() => {
    const schwere = (werte.schwere as string[]) ?? [];
    const raum = String(werte.raum ?? '').trim().toLowerCase();
    return zeilen.filter((z) => (schwere.length === 0 || schwere.includes(z.zeile.schwere))
      && (!werte.regel || z.zeile.regel === werte.regel)
      && (!werte.anlage || z.zeile.anlage === werte.anlage)
      && (!raum || (z.zeile.raumnummer ?? '').toLowerCase().includes(raum)));
  }, [zeilen, werte]);

  const spalten = useMemo<Spalte<Zeile>[]>(() => [
    {
      schluessel: 'schwere', titel: 'Schwere', art: 'status', immerSichtbar: true, wert: (z) => z.zeile.schwere,
      inhalt: (z) => <StatusBadge {...schwereStatus(z.zeile.schwere)} klein />,
    },
    { schluessel: 'regel', titel: 'Regel', wert: (z) => z.zeile.regel },
    { schluessel: 'anlage', titel: 'Anlage', wert: (z) => z.zeile.anlage ?? '' },
    { schluessel: 'weg', titel: 'Weg', wert: (z) => z.zeile.weg ?? '' },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.zeile.raumnummer ?? '' },
    {
      schluessel: 'ist', titel: 'Ist', art: 'zahl', wert: (z) => z.zeile.istwert,
      inhalt: (z) => zahlNachEinheit(z.zeile.istwert, z.zeile.einheit),
    },
    {
      schluessel: 'grenze', titel: 'Grenze', art: 'zahl', wert: (z) => z.zeile.grenzwert,
      inhalt: (z) => zahlNachEinheit(z.zeile.grenzwert, z.zeile.einheit),
    },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.zeile.einheit },
    { schluessel: 'norm', titel: 'Normbezug', wert: (z) => norm.get(z.zeile.regel)?.normbezug ?? '—' },
    { schluessel: 'meldung', titel: 'Meldung', wert: (z) => z.zeile.text },
    {
      schluessel: 'element', titel: 'Element', wert: (z) => String(z.zeile.kennung),
      inhalt: (z) => (
        <Elementverweis
          projektId={projekt.id} ziel={z.ziel} kennung={String(z.zeile.kennung)}
          ohne="Kanal oder Bauteil ohne Komponentenseite"
        />
      ),
    },
    {
      schluessel: 'seite', titel: 'Fachseite', sortierbar: false,
      inhalt: (z) => {
        const seiten = befundSeiten(z.zeile.regel);
        if (seiten.length === 0) return <span className="m-leise">—</span>;
        return (
          <span>
            {seiten.flatMap((s, i) => {
              const verweis = <Link key={s.pfad} to={`/projekte/${projekt.id}/${s.pfad}`}>{s.name}</Link>;
              return i === 0 ? [verweis] : [' · ', verweis];
            })}
          </span>
        );
      },
    },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.zeile.art ?? '', versteckt: true },
    { schluessel: 'bauform', titel: 'Bauform', wert: (z) => z.zeile.bauform ?? '', versteckt: true },
    { schluessel: 'kreis', titel: 'Kreis', wert: (z) => z.zeile.kreis ?? '', versteckt: true },
    { schluessel: 'geteilt', titel: 'Anlagen am Bauteil', art: 'zahl', nachkomma: 0, wert: (z) => z.zeile.geteilt, versteckt: true },
  ], [projekt.id, norm]);

  const regelzeilen = useMemo<Regelzeile[]>(() => (m?.regeln ?? []).map((r) => {
    const n = m?.jeRegel.find((x) => x.regel === r.regel);
    return { ...r, fehler: n?.fehler ?? 0, warnungen: n?.warnungen ?? 0, hinweise: n?.hinweise ?? 0 };
  }), [m]);

  const regelspalten = useMemo<Spalte<Regelzeile>[]>(() => [
    { schluessel: 'regel', titel: 'Regel', immerSichtbar: true, wert: (r) => r.regel },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (r) => r.bezeichnung },
    { schluessel: 'fehler', titel: 'Fehler', art: 'zahl', nachkomma: 0, wert: (r) => r.fehler },
    { schluessel: 'warnungen', titel: 'Warnungen', art: 'zahl', nachkomma: 0, wert: (r) => r.warnungen },
    { schluessel: 'hinweise', titel: 'Hinweise', art: 'zahl', nachkomma: 0, wert: (r) => r.hinweise },
    { schluessel: 'norm', titel: 'Normbezug', wert: (r) => r.normbezug },
    {
      schluessel: 'bestaetigt', titel: 'Grenzwert', art: 'status', wert: (r) => r.bestaetigt ?? '',
      inhalt: (r) => (r.bestaetigt === null
        ? <span className="m-leise">aus dem Rechenkern</span>
        : <StatusBadge {...bestaetigung(r.bestaetigt)} klein />),
    },
  ], []);

  const ohneLauf = !!daten && !daten.lauf;
  const gesperrt = !daten?.lauf ? { grund: 'Noch kein Lauf druckverlust-lueftung.' } : undefined;

  return (
    <>
      <PageHeader
        titel="Befunde Kanalnetz und Raumpegel"
        beschreibung="Alle Befunde des Druckverlustlaufs - Geschwindigkeiten, Kennfelder, Regler, Raumpegel, Hygiene - mit Normbezug und Verweis."
        meta={<Rechenstand lauf={daten?.lauf} datenhash={daten?.datenhash} />}
        aktionen={(
          <ExportLeiste ziele={[
            { art: 'excel', beschriftung: 'Excel', gesperrt, laden: () => api.lueftungPruefListen(projekt.id, 'befunde') },
          ]}
          />
        )}
      />

      {laedt && !daten && <LoadingState text="Befunde werden abgerufen …" zeilen={10} />}
      {fehler !== null && (
        <ErrorState meldung="Die Befunde konnten nicht abgerufen werden." technisch={fehler} beiErneutVersuchen={laden} />
      )}

      {ohneLauf && (
        <EmptyState
          titel="Noch keine Befunde aus dem Druckverlustlauf."
          erklaerung="Die Befunde entstehen mit dem Serverwerkzeug druckverlust-lueftung. Gibt es einen Lauf ohne jeden Befund, steht diese Seite ebenfalls leer."
        />
      )}

      {daten?.lauf && m && (
        <>
          <Vorbehalte eintraege={seitenVorbehalte([m])} />

          <KpiReihe>
            <KpiCard titel="Befunde" wert={m.gesamt} vergleich={`${formatiereZahl(m.regeln.length, 0)} Regeln`} />
            <KpiCard
              titel="Fehler" wert={m.fehler}
              zustand={m.fehler > 0 ? 'fehler' : 'gut'} zustandText={m.fehler > 0 ? 'Fehler' : 'keine'}
            />
            <KpiCard titel="Warnungen" wert={m.warnungen} />
            <KpiCard titel="Hinweise" wert={m.hinweise} />
          </KpiReihe>

          <ContentSection
            titel="Alle Befunde"
            beschreibung="Fehler zuerst, dann nach Regel und Anlage. Die Netz- und Wegtabellen stehen auf den Seiten Druckverlust und Schall."
            werkzeuge={<FilterBar filter={filter} werte={werte} beiAenderung={setWerte} inLeiste={4} />}
          >
            <DataTable<Zeile>
              name="Befunde Kanalnetz und Raumpegel"
              spalten={spalten}
              zeilen={gefiltert}
              schluessel={(z) => String(z.nr)}
              ausfuhr
              ausfuhrDateiname="befunde-kanalnetz-raumpegel.csv"
              suchePlatzhalter="Regel, Anlage, Raum, Meldung …"
              leer={(
                <EmptyState
                  klein
                  titel="Kein Befund passt zu den Filtern."
                  erklaerung="Der Lauf hat Befunde; die gesetzten Filter schließen alle aus."
                />
              )}
            />
          </ContentSection>

          <ContentSection titel="Regeln und Normbezug" beschreibung="Je Regel die Zahl der Befunde, die Quelle des Grenzwerts und seine Bestätigung.">
            <DataTable<Regelzeile>
              name="Regeln der Befunde"
              spalten={regelspalten}
              zeilen={regelzeilen}
              schluessel={(r) => r.regel}
              leer={<span>Keine Regel hat einen Befund.</span>}
            />
          </ContentSection>

          <AnnahmenAbschnitt annahmen={m.annahmen} name="Befunde" />
        </>
      )}
    </>
  );
}
